// ---------------------------------------------------------------------------
// Signers constants
// ---------------------------------------------------------------------------
import { t } from "i18next";

export const MAX_INCOMES_PER_PERSON = 3;

// Option labels are resolved here via t() (English-only app; i18n is
// initialized before this module evaluates — see src/i18n.ts / setupTests.ts).
// `value` is the canonical EMPLOYMENT_TYPES code (matches CAPS/dealer-ui) so the
// form speaks the same language as the BFF. Copy lives in src/languages/en.json.

// ---------------------------------------------------------------------------
// Income Type options
// ---------------------------------------------------------------------------
export const INCOME_TYPE_OPTIONS = [
  { value: "6", label: t("review.options.incomeType.employee") },
  { value: "7", label: t("review.options.incomeType.selfEmployed") },
  { value: "8", label: t("review.options.incomeType.fixedIncome") },
  { value: "9", label: t("review.options.incomeType.tempServiceEmployee") },
  { value: "10", label: t("review.options.incomeType.unemployed") },
  { value: "11", label: t("review.options.incomeType.cashIncome") },
] as const;

export type IncomeTypeOption = (typeof INCOME_TYPE_OPTIONS)[number];

// ---------------------------------------------------------------------------
// Income Type filtering by applicant SSN/ITIN status
// ---------------------------------------------------------------------------
export const APPLICANT_SSN_TYPES = {
  SSN: "ssn",
  NO_ITIN_SSN: "noItinSsn",
  ITIN: "itin",
} as const;

export type ApplicantSsnType = (typeof APPLICANT_SSN_TYPES)[keyof typeof APPLICANT_SSN_TYPES];

// Income-type codes a non-SSN applicant may select: Self-Employed (7),
// Unemployed (10), Cash Income (11).
const NON_SSN_INCOME_TYPE_CODES = new Set<IncomeTypeOption["value"]>(["7", "10", "11"]);

/**
 * Income-type options filtered by the applicant's SSN/ITIN status, mirroring
 * dealer-ui's `getIncomeTypeOptions`: an applicant without a validated SSN
 * (no-SSN/ITIN or ITIN) may only pick Self-Employed, Unemployed, or Cash
 * Income; an SSN applicant gets the full list.
 *
 * `applicantSsnType` is derived from the dashboard `residence` section's
 * `isNoItinSsn` / `isSsn` flags (see `deriveApplicantSsnType` in
 * residenceMapping.ts, step6-bff-client@1.0.287) — an unknown/ITIN applicant
 * falls to the restricted list, matching dealer-ui.
 * @param {ApplicantSsnType} applicantSsnType - The applicant's SSN/ITIN status.
 * @returns {IncomeTypeOption[]} The income-type options allowed for that status.
 */
export function getIncomeTypeOptions(applicantSsnType: ApplicantSsnType): IncomeTypeOption[] {
  if (applicantSsnType === APPLICANT_SSN_TYPES.SSN) {
    return [...INCOME_TYPE_OPTIONS];
  }
  return INCOME_TYPE_OPTIONS.filter((option) => NON_SSN_INCOME_TYPE_CODES.has(option.value));
}

/**
 * Whether an income-type code is selectable for the given SSN/ITIN status.
 * Used to clamp a hydrated income type that is no longer valid for a non-SSN
 * applicant (see incomeMapping).
 * @param {string} value - The income-type code (EMPLOYMENT_TYPES id).
 * @param {ApplicantSsnType} applicantSsnType - The applicant's SSN/ITIN status.
 * @returns {boolean} True when the code is allowed for that status.
 */
export function isIncomeTypeAllowed(value: string, applicantSsnType: ApplicantSsnType): boolean {
  return getIncomeTypeOptions(applicantSsnType).some((option) => option.value === value);
}

/**
 * The default income-type code for a freshly seeded income block, honoring the
 * applicant's SSN/ITIN status: the first allowed option (Employee "6" for an
 * SSN applicant, Self-Employed "7" for a non-SSN one).
 * @param {ApplicantSsnType} applicantSsnType - The applicant's SSN/ITIN status.
 * @returns {IncomeTypeOption["value"]} The default income-type code.
 */
export function getDefaultIncomeType(applicantSsnType: ApplicantSsnType): IncomeTypeOption["value"] {
  return getIncomeTypeOptions(applicantSsnType)[0]?.value ?? "6";
}

// ---------------------------------------------------------------------------
// Pay Frequency options (values match the dealer-ui PAY_FREQUENCY literals)
// ---------------------------------------------------------------------------
export const PAY_FREQUENCY_OPTIONS = [
  { value: "Weekly", label: t("review.options.payFrequency.weekly") },
  { value: "Bi-Weekly", label: t("review.options.payFrequency.biWeekly") },
  { value: "2x-Monthly", label: t("review.options.payFrequency.twiceMonthly") },
  { value: "Monthly", label: t("review.options.payFrequency.monthly") },
  { value: "Annual", label: t("review.options.payFrequency.annual") },
] as const;

// ---------------------------------------------------------------------------
// Gross / Net options (values match the dealer-ui PAY_TYPE literals)
// ---------------------------------------------------------------------------
export const GROSS_NET_OPTIONS = [
  { value: "Gross", label: t("review.options.grossNet.gross") },
  { value: "Net", label: t("review.options.grossNet.net") },
] as const;

// ---------------------------------------------------------------------------
// US States — static list (BFF/dynamic replacement in a later story).
// Labelled by abbreviation (label === value) so the select shows "MI", not
// "Michigan" — matches how State is captured/displayed elsewhere.
// ---------------------------------------------------------------------------
export const US_STATE_OPTIONS = [
  { value: "AL", label: "AL" },
  { value: "AK", label: "AK" },
  { value: "AZ", label: "AZ" },
  { value: "AR", label: "AR" },
  { value: "CA", label: "CA" },
  { value: "CO", label: "CO" },
  { value: "CT", label: "CT" },
  { value: "DE", label: "DE" },
  { value: "FL", label: "FL" },
  { value: "GA", label: "GA" },
  { value: "HI", label: "HI" },
  { value: "ID", label: "ID" },
  { value: "IL", label: "IL" },
  { value: "IN", label: "IN" },
  { value: "IA", label: "IA" },
  { value: "KS", label: "KS" },
  { value: "KY", label: "KY" },
  { value: "LA", label: "LA" },
  { value: "ME", label: "ME" },
  { value: "MD", label: "MD" },
  { value: "MA", label: "MA" },
  { value: "MI", label: "MI" },
  { value: "MN", label: "MN" },
  { value: "MS", label: "MS" },
  { value: "MO", label: "MO" },
  { value: "MT", label: "MT" },
  { value: "NE", label: "NE" },
  { value: "NV", label: "NV" },
  { value: "NH", label: "NH" },
  { value: "NJ", label: "NJ" },
  { value: "NM", label: "NM" },
  { value: "NY", label: "NY" },
  { value: "NC", label: "NC" },
  { value: "ND", label: "ND" },
  { value: "OH", label: "OH" },
  { value: "OK", label: "OK" },
  { value: "OR", label: "OR" },
  { value: "PA", label: "PA" },
  { value: "RI", label: "RI" },
  { value: "SC", label: "SC" },
  { value: "SD", label: "SD" },
  { value: "TN", label: "TN" },
  { value: "TX", label: "TX" },
  { value: "UT", label: "UT" },
  { value: "VT", label: "VT" },
  { value: "VA", label: "VA" },
  { value: "WA", label: "WA" },
  { value: "WV", label: "WV" },
  { value: "WI", label: "WI" },
  { value: "WY", label: "WY" },
  { value: "DC", label: "DC" },
] as const;

// ---------------------------------------------------------------------------
// Placeholder city list — static (BFF/dynamic replacement in a later story)
// ---------------------------------------------------------------------------
// City options are sourced from real data at runtime; no sample/seed values here.
export const CITY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [];

// ---------------------------------------------------------------------------
// Ordinal labels for income block headers
// ---------------------------------------------------------------------------
export const INCOME_ORDINAL_LABELS = ["1st", "2nd", "3rd"] as const;

// ---------------------------------------------------------------------------
// Bank Information constants
// ---------------------------------------------------------------------------

// Letters, spaces, forward-slash, and question-mark only (mirrors dealer-ui).
export const BANK_NAME_REGEX = /^[\s/?A-Za-z]*$/;

export const BANK_NAME_MAX_LENGTH = 100;

// Account-type checkbox option list: field-key + i18n-resolved label.
// Order matches dealer-ui / Figma: Savings → Checking → Prepaid Debit Card.
export const ACCOUNT_TYPE_OPTIONS = [
  { field: "isSavingsAccount" as const, label: t("review.bank.accountType.savings") },
  { field: "isCheckingAccount" as const, label: t("review.bank.accountType.checking") },
  { field: "isPrepaidDebit" as const, label: t("review.bank.accountType.prepaidDebit") },
] as const;

// ---------------------------------------------------------------------------
// Customer Information constants (OR-21996)
// ---------------------------------------------------------------------------

export const MAX_PHONES_PER_PERSON = 5;

export const PHONE_EXTENSION_MAX_LENGTH = 4;

export const PHONE_NUMBER_DIGITS = 10;

// Hold Coupon Book select options (No/Yes)
export const HOLD_COUPON_BOOK_OPTIONS = [
  { value: "No", label: t("review.customer.options.holdCouponBook.no") },
  { value: "Yes", label: t("review.customer.options.holdCouponBook.yes") },
] as const;

// Phone type select options (Personal/Work)
export const PHONE_TYPE_OPTIONS = [
  { value: "Personal", label: t("review.customer.options.phoneType.personal") },
  { value: "Work", label: t("review.customer.options.phoneType.work") },
] as const;

// Name suffix select options — matches dealer-ui NAME_SUFFIX_OPTION_VALUES.
// Empty string "" is the "None" placeholder value (no suffix selected).
export const SUFFIX_OPTIONS = [
  { value: "", label: t("review.customer.options.suffix.none") },
  { value: "I", label: "I" },
  { value: "II", label: "II" },
  { value: "III", label: "III" },
  { value: "IV", label: "IV" },
  { value: "Sr.", label: "Sr." },
  { value: "Jr.", label: "Jr." },
] as const;
